import type { PushSubscriptionBody } from '../types/domain.types';

import { applicationServerKeyOf, subscriptionBodyOf } from './subscription';

export type PushPermission = NotificationPermission;

export interface PushEnvironment {
  readonly supported: boolean;
  readonly standalone: boolean;
  readonly permission: PushPermission;
}

export type DeviceNotifications =
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'notInstalled' }
  | { readonly kind: 'denied' }
  | { readonly kind: 'off' }
  | { readonly kind: 'on'; readonly endpoint: string };

export interface PushPort {
  readonly environment: () => PushEnvironment;
  readonly register: () => Promise<ServiceWorkerRegistration>;
  readonly existingRegistration: () => Promise<ServiceWorkerRegistration | undefined>;
  readonly vapidKey: () => Promise<string>;
  readonly save: (body: PushSubscriptionBody) => Promise<unknown>;
  readonly remove: (endpoint: string) => Promise<void>;
  readonly userAgent: () => string;
}

const UNSUPPORTED: DeviceNotifications = { kind: 'unsupported' };
const NOT_INSTALLED: DeviceNotifications = { kind: 'notInstalled' };
const DENIED: DeviceNotifications = { kind: 'denied' };
const OFF: DeviceNotifications = { kind: 'off' };

export const canAskForPush = (env: PushEnvironment, askedHere: boolean): boolean =>
  env.supported && env.standalone && env.permission === 'default' && !askedHere;

const undecided = (env: PushEnvironment): DeviceNotifications => (env.standalone ? OFF : NOT_INSTALLED);

const registrationOf = async (port: PushPort): Promise<ServiceWorkerRegistration | null> => {
  try {
    return await port.register();
  } catch {
    return null;
  }
};

const withdraw = async (subscription: PushSubscription): Promise<void> => {
  try {
    await subscription.unsubscribe();
  } catch {
    return;
  }
};

const subscribeAndSave = async (port: PushPort, registration: ServiceWorkerRegistration, env: PushEnvironment): Promise<DeviceNotifications> => {
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKeyOf(await port.vapidKey()),
    }));
  try {
    await port.save(subscriptionBodyOf(subscription, { userAgent: port.userAgent(), installed: env.standalone }));
  } catch (error) {
    if (existing === null) await withdraw(subscription);
    throw error;
  }
  return { kind: 'on', endpoint: subscription.endpoint };
};

export const readThisDevice = async (port: PushPort, signal?: AbortSignal): Promise<DeviceNotifications> => {
  const env = port.environment();
  if (!env.supported) return UNSUPPORTED;
  if (env.permission === 'denied') return DENIED;
  if (env.permission === 'default') return undecided(env);
  const registration = await port.existingRegistration();
  signal?.throwIfAborted();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? { kind: 'on', endpoint: subscription.endpoint } : OFF;
};

export const syncThisDevice = async (port: PushPort): Promise<DeviceNotifications> => {
  const env = port.environment();
  if (!env.supported) return UNSUPPORTED;
  const registration = await registrationOf(port);
  if (registration === null) return UNSUPPORTED;
  if (env.permission === 'denied') return DENIED;
  if (env.permission === 'default') return undecided(env);
  return subscribeAndSave(port, registration, env);
};

export const turnOnThisDevice = async (port: PushPort, asking: Promise<PushPermission>): Promise<DeviceNotifications> => {
  const permission = await asking;
  const env = { ...port.environment(), permission };
  if (!env.supported) return UNSUPPORTED;
  if (permission === 'denied') return DENIED;
  if (permission === 'default') return undecided(env);
  const registration = await registrationOf(port);
  if (registration === null) return UNSUPPORTED;
  return subscribeAndSave(port, registration, env);
};

const removeSubscription = async (port: PushPort, registration: ServiceWorkerRegistration | undefined): Promise<void> => {
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  await port.remove(subscription.endpoint);
  await subscription.unsubscribe();
};

export const turnOffThisDevice = async (port: PushPort): Promise<DeviceNotifications> => {
  const env = port.environment();
  if (!env.supported) return UNSUPPORTED;
  await removeSubscription(port, (await registrationOf(port)) ?? undefined);
  return env.permission === 'denied' ? DENIED : OFF;
};

export const forgetThisDevice = async (port: PushPort): Promise<void> => {
  if (!port.environment().supported) return;
  const subscription = await (await port.existingRegistration())?.pushManager.getSubscription();
  if (!subscription) return;
  try {
    await port.remove(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
};
